import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';

import { ACCOUNT_SUBSCRIPTION_SEED, ACCOUNT_TIERS, PROPERTIES } from '@common/data';
import { TIER_FEATURE_CATALOG, TIER_LIMIT_CATALOG } from '@common/enums';
import type {
  AccountSubscription,
  AccountTier,
  AccountTierKey,
  TierLimitValue,
} from '@common/types';

import { AdminAccountService } from './admin-account.service';

const SAVE_LATENCY_MS = 250;
const TIER_ROTATION: ReadonlyArray<AccountTierKey> = ['core', 'pro', 'lite'];
const STATUS_ROTATION: ReadonlyArray<AccountSubscription['status']> = [
  'active',
  'active',
  'trialing',
  'active',
  'past_due',
];

function buildSubscriptions(): Record<string, AccountSubscription> {
  const map: Record<string, AccountSubscription> = {};
  PROPERTIES.forEach((account, index) => {
    const seats = 6 + ((index * 3) % 18);
    map[account.id] = {
      ...ACCOUNT_SUBSCRIPTION_SEED,
      accountId: account.id,
      tierKey: TIER_ROTATION[index % TIER_ROTATION.length],
      status: STATUS_ROTATION[index % STATUS_ROTATION.length],
      seatsUsed: seats,
      usage: {
        seats,
        'monthly-reviews': 800 + index * 220,
        'ai-credits': index % 3 === 1 ? 1500 + index * 60 : 0,
        'connected-sources': 3 + (index % 6),
      },
    };
  });
  return map;
}

@Injectable({ providedIn: 'root' })
export class AccountTierService {
  private readonly admin = inject(AdminAccountService);

  readonly tiers: ReadonlyArray<AccountTier> = ACCOUNT_TIERS;
  readonly featureCatalog = TIER_FEATURE_CATALOG;
  readonly limitCatalog = TIER_LIMIT_CATALOG;
  readonly meteredLimits = TIER_LIMIT_CATALOG.filter((limit) => limit.metered);

  private readonly _subscriptions = signal<Record<string, AccountSubscription>>(
    buildSubscriptions(),
  );

  readonly subscription = computed<AccountSubscription>(() => {
    const map = this._subscriptions();
    const id = this.admin.accountId();
    if (id && map[id]) return map[id];
    return Object.values(map)[0] ?? ACCOUNT_SUBSCRIPTION_SEED;
  });

  readonly currentTier = computed<AccountTier | undefined>(() =>
    this.tierByKey(this.subscription().tierKey),
  );

  subscriptionFor(accountId: string): AccountSubscription | undefined {
    return this._subscriptions()[accountId];
  }

  tierForAccount(accountId: string): AccountTier | undefined {
    const subscription = this.subscriptionFor(accountId);
    return subscription ? this.tierByKey(subscription.tierKey) : undefined;
  }

  tierByKey(key: AccountTierKey): AccountTier | undefined {
    return this.tiers.find((tier) => tier.key === key);
  }

  hasFeature(tierKey: AccountTierKey, featureKey: string): boolean {
    return this.tierByKey(tierKey)?.featureAccess[featureKey] ?? false;
  }

  limitFor(tierKey: AccountTierKey, limitKey: string): TierLimitValue {
    return this.tierByKey(tierKey)?.limits[limitKey] ?? 0;
  }

  usageFor(limitKey: string): number {
    return this.subscription().usage[limitKey] ?? 0;
  }

  changeTier(key: AccountTierKey): Observable<AccountSubscription> {
    const target = this.tierByKey(key);
    if (!target) return throwError(() => new Error('Tier not found.'));
    const current = this.subscription();
    if (current.tierKey === key) return of(current).pipe(delay(SAVE_LATENCY_MS));
    const next: AccountSubscription = { ...current, tierKey: key };
    this._subscriptions.update((map) => ({ ...map, [current.accountId]: next }));
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }
}
